import {
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
	Component,
} from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Layout } from "../components/Layout";
import AthleteDashboard from "../components/AthleteDashboard";
import { PushOptInBanner } from "../components/PushOptInBanner";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useNotificationCounts } from "../contexts/NotificationCountsContext";
import { fetchAPI } from "../lib/utils";
import { usePushOptIn } from "../lib/usePushOptIn";
import {
	buildAthletePreviewProgram,
	toISODate,
} from "../lib/programVisibility";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ExerciseDetailAccordion } from "./workouts/components/ExerciseDetailAccordion";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../components/ui/dialog";
import {
	Archive,
	ArchiveRestore,
	ArrowLeft,
	Check,
	CheckCheck,
	Plus,
	Send,
	Dumbbell,
	User,
	MessageSquare,
	Loader2,
	AlertTriangle,
	Search,
	ChevronDown,
	ExternalLink,
	LifeBuoy,
	Eye,
	EyeOff,
	Trash2,
} from "lucide-react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const THREAD_POLL_MS = 12000;

/** Guards the reused diary detail component so unusual data can't blank the page. */
class DetailBoundary extends Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	render() {
		if (this.state.hasError) return null;
		return this.props.children;
	}
}

export default function Messages() {
	const { user } = useAuth();
	const { t, language } = useLanguage();
	const { refreshMessageUnreadCount } = useNotificationCounts();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const me = user?.user_id;
	const [detailOpen, setDetailOpen] = useState(false);
	const [contextOpen, setContextOpen] = useState(false);

	const pushOptIn = usePushOptIn();

	const [threads, setThreads] = useState([]);
	const [loadingThreads, setLoadingThreads] = useState(true);
	const [threadsHasMore, setThreadsHasMore] = useState(false);
	const [loadingMoreThreads, setLoadingMoreThreads] = useState(false);

	// Archive / delete state
	const [showArchived, setShowArchived] = useState(false);
	const [confirmDeleteThread, setConfirmDeleteThread] = useState(null);
	const [threadActionLoading, setThreadActionLoading] = useState(null); // thread_id being processed
	// Swipe-to-reveal state (mobile)
	const SNAP_WIDTH = 130;
	const SWIPE_THRESHOLD = 55;
	const swipeRef = useRef(null); // { id, startX, startY, currentDx }
	const [swipedOpen, setSwipedOpen] = useState(null);
	const [liveSwipe, setLiveSwipe] = useState(null); // { id, x } during active drag

	const [active, setActive] = useState(null); // thread context (+ draft flag)
	const isFeedbackThread =
		active?.kind === "feedback" || active?.kind === "support";
	const [messages, setMessages] = useState([]);
	const [loadingThread, setLoadingThread] = useState(false);

	const [text, setText] = useState("");
	const [sending, setSending] = useState(false);

	const [contacts, setContacts] = useState([]);
	const [contactSearchQuery, setContactSearchQuery] = useState("");
	const [newOpen, setNewOpen] = useState(false);
	const [programPreviewOpen, setProgramPreviewOpen] = useState(false);
	const [programPreviewLoading, setProgramPreviewLoading] = useState(false);
	const [programPreviewState, setProgramPreviewState] = useState(null);

	const scrollRef = useRef(null);

	const openCoachProgramPreview = async () => {
		if (!active?.program_id || !active?.partner_id) {
			toast.error(t("feedbackLoadError"));
			return;
		}

		setProgramPreviewOpen(true);
		setProgramPreviewLoading(true);
		try {
			const [programData, exercises] = await Promise.all([
				fetchAPI(`/programs/${active.program_id}`),
				fetchAPI("/exercises?scope=visible").catch(() => []),
			]);

			const todayIso = toISODate(new Date());
			const previewProgram = buildAthletePreviewProgram(programData, {
				todayIso,
				athleteId: active.partner_id,
			});
			const dayWeekIndex = Array.isArray(previewProgram?.weeks)
				? previewProgram.weeks.findIndex((week) =>
						(week?.days || []).some((day) => day?.day_id === active?.day_id),
					)
				: 0;
			// The server already says so on the thread; this covers the rest (the
			// week expired since the last poll): never fall back on today's week.
			if (active?.day_id && dayWeekIndex === -1) {
				setProgramPreviewOpen(false);
				setProgramPreviewState(null);
				setActive((prev) =>
					prev ? { ...prev, program_preview: "expired" } : prev,
				);
				toast.info(t("msgProgramNoLongerActive"));
				return;
			}
			const weekIndex = Math.max(0, dayWeekIndex);

			setProgramPreviewState({
				program: previewProgram,
				programs: previewProgram ? [previewProgram] : [],
				exercises: Array.isArray(exercises) ? exercises : [],
				selectedProgramId: previewProgram?.program_id || active.program_id,
				weekIndex,
				dayId: active?.day_id || null,
			});
		} catch (e) {
			setProgramPreviewState(null);
			toast.error(t("feedbackLoadError"));
		} finally {
			setProgramPreviewLoading(false);
		}
	};

	const loadThreads = useCallback(
		async (folder) => {
			const f =
				folder !== undefined ? folder : showArchived ? "archived" : "inbox";
			try {
				// Paginated: first page only. Old threads load on demand ("load more").
				const data = await fetchAPI(
					`/messages/threads?folder=${f}&limit=30&offset=0`,
				);
				const list = Array.isArray(data) ? data : [];
				setThreads(list);
				setThreadsHasMore(list.length === 30);
			} catch (e) {
				/* keep previous */
			} finally {
				setLoadingThreads(false);
			}
		},
		[showArchived],
	);

	const loadMoreThreads = useCallback(async () => {
		if (loadingMoreThreads || !threadsHasMore) return;
		const f = showArchived ? "archived" : "inbox";
		setLoadingMoreThreads(true);
		try {
			const data = await fetchAPI(
				`/messages/threads?folder=${f}&limit=30&offset=${threads.length}`,
			);
			const list = Array.isArray(data) ? data : [];
			setThreads((prev) => {
				const seen = new Set(prev.map((th) => th.thread_id));
				return [...prev, ...list.filter((th) => !seen.has(th.thread_id))];
			});
			setThreadsHasMore(list.length === 30);
		} catch (e) {
			/* keep current */
		} finally {
			setLoadingMoreThreads(false);
		}
	}, [showArchived, threads.length, loadingMoreThreads, threadsHasMore]);

	useEffect(() => {
		loadThreads();
	}, [loadThreads]);

	const scrollToBottom = () => {
		requestAnimationFrame(() => {
			if (scrollRef.current)
				scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		});
	};

	const loadThreadMessages = useCallback(
		async (threadId, { silent = false } = {}) => {
			if (!threadId) return;
			if (!silent) setLoadingThread(true);
			try {
				const data = await fetchAPI(
					`/messages/thread/${encodeURIComponent(threadId)}`,
				);
				setMessages(Array.isArray(data?.messages) ? data.messages : []);
				setActive((prev) =>
					prev ? { ...prev, ...data, draft: false } : { ...data, draft: false },
				);
				scrollToBottom();
			} catch (e) {
				if (!silent) toast.error(t("msgLoadError"));
			} finally {
				if (!silent) setLoadingThread(false);
			}
		},
		[t],
	);

	const openThread = (thread) => {
		setDetailOpen(false);
		setContextOpen(false);
		setActive({ ...thread, draft: false });
		setMessages([]);
		loadThreadMessages(thread.thread_id);
		// Opening clears unread → refresh list shortly after.
		setTimeout(() => {
			loadThreads();
			refreshMessageUnreadCount();
		}, 600);
	};

	// Light polling while a (non-draft) thread is open.
	useEffect(() => {
		if (!active || active.draft || !active.thread_id) return;
		const id = setInterval(
			() => loadThreadMessages(active.thread_id, { silent: true }),
			THREAD_POLL_MS,
		);
		return () => clearInterval(id);
	}, [active, loadThreadMessages]);

	const openContacts = async () => {
		setNewOpen(true);
		setContactSearchQuery("");
		try {
			const data = await fetchAPI("/messages/contacts");
			setContacts(Array.isArray(data) ? data : []);
		} catch (e) {
			setContacts([]);
		}
	};

	const filteredContacts = useMemo(() => {
		const query = String(contactSearchQuery || "")
			.trim()
			.toLowerCase();
		if (!query) return contacts;

		return contacts.filter((contact) => {
			const fullName = String(contact?.name || "").toLowerCase();
			const nickname = String(contact?.nickname || "").toLowerCase();
			const email = String(contact?.email || "").toLowerCase();
			return (
				fullName.includes(query) ||
				nickname.includes(query) ||
				email.includes(query)
			);
		});
	}, [contacts, contactSearchQuery]);

	const startDirect = (contact) => {
		setNewOpen(false);
		setContactSearchQuery("");
		const threadId = "dm:" + [me, contact.user_id].sort().join(":");
		const existing = threads.find((th) => th.thread_id === threadId);
		if (existing) {
			openThread(existing);
			return;
		}
		const draft = {
			thread_id: threadId,
			partner_id: contact.user_id,
			partner_name: contact.name,
			kind: "direct",
			draft: true,
		};
		setActive(draft);
		setMessages([]);
		// Arrivando da un'altra maschera (Monitoraggio) l'elenco delle
		// conversazioni puo' non essere ancora caricato: se la conversazione
		// esiste gia', la si apre con la sua storia invece di una bozza vuota.
		fetchAPI(`/messages/thread/${encodeURIComponent(threadId)}`)
			.then((data) => {
				if (!data?.messages?.length) return;
				setActive((current) =>
					current?.thread_id === threadId ? { ...draft, ...data, draft: false } : current,
				);
				setMessages(data.messages);
			})
			.catch(() => {});
	};

	// ---------- ARCHIVE / DELETE ----------
	const archiveThread = async (thread, archive = true) => {
		const tid = thread.thread_id;
		setThreadActionLoading(tid);
		try {
			await fetchAPI("/messages/thread-action", {
				method: "POST",
				body: JSON.stringify({
					thread_id: tid,
					action: archive ? "archive" : "unarchive",
				}),
			});
			toast.success(archive ? t("msgArchivedDone") : t("msgUnarchivedDone"));
			setSwipedOpen(null);
			loadThreads();
		} catch (e) {
			toast.error(t("msgActionError"));
		} finally {
			setThreadActionLoading(null);
		}
	};

	const deleteThreadForMe = async (thread) => {
		const tid = thread.thread_id;
		setThreadActionLoading(tid);
		try {
			await fetchAPI("/messages/thread-action", {
				method: "POST",
				body: JSON.stringify({ thread_id: tid, action: "delete" }),
			});
			toast.success(t("msgDeletedDone"));
			setConfirmDeleteThread(null);
			setSwipedOpen(null);
			if (active?.thread_id === tid) {
				setActive(null);
				setMessages([]);
			}
			loadThreads();
		} catch (e) {
			toast.error(t("msgActionError"));
		} finally {
			setThreadActionLoading(null);
		}
	};

	// ---------- SWIPE HANDLERS (mobile) ----------
	const handleTouchStart = (e, id) => {
		const touch = e.touches[0];
		swipeRef.current = {
			id,
			startX: touch.clientX,
			startY: touch.clientY,
			currentDx: 0,
		};
		// Close any other swiped row
		if (swipedOpen && swipedOpen !== id) setSwipedOpen(null);
	};

	const handleTouchMove = (e, id) => {
		const ref = swipeRef.current;
		if (!ref || ref.id !== id) return;
		const touch = e.touches[0];
		const dx = touch.clientX - ref.startX;
		const dy = touch.clientY - ref.startY;
		// Prioritise vertical scroll if clearly vertical
		if (Math.abs(dy) > Math.abs(dx) + 8 && Math.abs(dx) < 15) {
			swipeRef.current = null;
			return;
		}
		// Only allow left swipe (negative dx) unless already swiped open (allow close)
		const base = swipedOpen === id ? -SNAP_WIDTH : 0;
		const newX = Math.max(-SNAP_WIDTH, Math.min(0, base + dx));
		ref.currentDx = dx;
		setLiveSwipe({ id, x: newX });
		// Prevent page scroll while swiping horizontally
		if (Math.abs(dx) > 10) {
			try {
				e.preventDefault();
			} catch (_) {}
		}
	};

	const handleTouchEnd = (_e, id) => {
		const ref = swipeRef.current;
		swipeRef.current = null;
		setLiveSwipe(null);
		if (!ref || ref.id !== id) return;
		const base = swipedOpen === id ? -SNAP_WIDTH : 0;
		const result = Math.max(-SNAP_WIDTH, Math.min(0, base + ref.currentDx));
		setSwipedOpen(result < -SWIPE_THRESHOLD ? id : null);
	};

	const getRowTranslate = (id) => {
		if (liveSwipe?.id === id) return liveSwipe.x;
		return swipedOpen === id ? -SNAP_WIDTH : 0;
	};

	const send = async () => {
		const content = text.trim();
		if (!content || !active || sending) return;
		if (active.kind === "support" && !active.partner_id) {
			toast.error(t("msgSendError"));
			return;
		}
		setSending(true);
		try {
			const body = {
				receiver_id: active.partner_id,
				thread_id: active.thread_id,
				content,
				message_type:
					active.kind === "feedback" ? "workout_feedback" : "general",
			};
			// Support threads carry the exercise reference on the first message.
			if (active.kind === "support") {
				body.related_program_id = active.program_id || null;
				body.related_day_id = active.day_id || null;
				body.related_exercise_id = active.exercise_id || null;
				body.related_exercise_name = active.exercise_name || null;
			}
			await fetchAPI("/messages", {
				method: "POST",
				body: JSON.stringify(body),
			});
			setText("");
			await loadThreadMessages(active.thread_id, { silent: true });
			loadThreads();
			refreshMessageUnreadCount();
		} catch (e) {
			toast.error(e?.message || t("msgSendError"));
		} finally {
			setSending(false);
		}
	};

	// Deep-link handling: open a specific thread, or start a support/direct draft.
	useEffect(() => {
		const st = location.state;
		if (!st) return;
		if (st.openThreadId) {
			openThread({ thread_id: st.openThreadId });
		} else if (st.support) {
			const s = st.support;
			const threadId = `sup:${s.program_id || ""}:${s.day_id || ""}:${s.exercise_id || ""}`;
			const draft = {
				thread_id: threadId,
				kind: "support",
				partner_id: s.coach_id || null,
				partner_name: s.coach_name || null,
				exercise_name: s.exercise_name || null,
				exercise_id: s.exercise_id || null,
				day_id: s.day_id || null,
				program_id: s.program_id || null,
				workout_date: s.date || null,
				draft: true,
			};
			(async () => {
				try {
					const data = await fetchAPI(
						`/messages/thread/${encodeURIComponent(threadId)}`,
					);
					if (data?.messages?.length) {
						setActive({ ...draft, ...data, draft: false });
						setMessages(data.messages);
					} else {
						setActive(draft);
						setMessages([]);
					}
				} catch (e) {
					setActive(draft);
					setMessages([]);
				}
			})();
		} else if (st.contact) {
			startDirect(st.contact);
		}
		navigate(location.pathname, { replace: true, state: null });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.key]);

	useEffect(() => {
		const threadId = searchParams.get("threadId");
		if (!threadId) return;
		openThread({ thread_id: threadId });
		navigate(location.pathname, { replace: true, state: null });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams, location.pathname]);

	// --- formatting helpers ---
	const fmtTime = (iso) => {
		if (!iso) return "";
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return "";
		return d.toLocaleTimeString(language === "it" ? "it-IT" : "en-US", {
			hour: "2-digit",
			minute: "2-digit",
		});
	};
	const relTime = (iso) => {
		if (!iso) return "";
		const then = new Date(iso).getTime();
		if (Number.isNaN(then)) return "";
		const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
		if (mins < 1) return language === "it" ? "ora" : "now";
		if (mins < 60) return `${mins}m`;
		const hrs = Math.round(mins / 60);
		if (hrs < 24) return `${hrs}h`;
		const days = Math.round(hrs / 24);
		if (days < 7) return language === "it" ? `${days}g` : `${days}d`;
		try {
			return new Date(iso).toLocaleDateString(
				language === "it" ? "it-IT" : "en-US",
				{
					day: "numeric",
					month: "short",
				},
			);
		} catch (e) {
			return "";
		}
	};
	const dayLabel = (iso) => {
		const d = new Date(iso);
		if (Number.isNaN(d.getTime())) return "";
		const today = new Date();
		const y = new Date();
		y.setDate(today.getDate() - 1);
		const k = d.toDateString();
		if (k === today.toDateString()) return t("today");
		if (k === y.toDateString()) return t("yesterday");
		return d.toLocaleDateString(language === "it" ? "it-IT" : "en-US", {
			weekday: "short",
			day: "numeric",
			month: "short",
		});
	};
	const toText = (value) => String(value ?? "").trim();
	const hasMeaningful = (value) => {
		const text = toText(value);
		return text !== "" && text !== "0" && text !== "0.0";
	};
	const formatResultSummary = (result) => {
		if (!result || typeof result !== "object") return "";
		const parts = [];
		if (hasMeaningful(result.weight_kg)) parts.push(`${result.weight_kg} kg`);
		if (hasMeaningful(result.reps_text)) parts.push(result.reps_text);
		else if (hasMeaningful(result.reps)) parts.push(`${result.reps} reps`);
		if (hasMeaningful(result.time_seconds)) {
			const totalSeconds = Number(result.time_seconds);
			if (Number.isFinite(totalSeconds) && totalSeconds >= 0) {
				const mm = Math.floor(totalSeconds / 60);
				const ss = Math.round(totalSeconds % 60);
				parts.push(`${mm}:${String(ss).padStart(2, "0")}`);
			}
		}
		if (hasMeaningful(result.score)) parts.push(String(result.score));
		if (hasMeaningful(result.rpe)) parts.push(`RPE ${result.rpe}`);
		if (hasMeaningful(result.scaling)) parts.push(result.scaling);
		return parts.join(" · ");
	};

	const getDeliveryState = (message) => {
		if (!message || typeof message !== "object") return "sent";
		if (
			message.is_read ||
			message.read_at ||
			message.delivery_status === "read"
		)
			return "read";
		if (message.delivered_at || message.delivery_status === "delivered")
			return "delivered";
		return "sent";
	};

	const threadTitle = (th) =>
		th.kind === "feedback"
			? th.exercise_name || t("feedback")
			: th.kind === "support"
				? th.exercise_name || t("msgSupportRequest")
				: th.partner_name || "—";

	// ---------- THREAD LIST ----------
	const renderList = () => (
		<div className="flex flex-col h-full">
			{/* Header */}
			<div className="flex items-center justify-between px-4 pt-5 pb-2">
				<h1 className="text-2xl font-heading font-extrabold uppercase tracking-tight flex items-center gap-2">
					<MessageSquare className="w-6 h-6 text-volt" />
					{showArchived ? t("msgArchived") : t("messages")}
				</h1>
				{!showArchived && (
					<Button
						size="sm"
						onClick={openContacts}
						className="bg-volt text-black font-bold rounded-sm hover:bg-volt/90"
					>
						<Plus className="w-4 h-4 mr-1" />
						{t("msgNew")}
					</Button>
				)}
			</div>

			{/* Inbox / Archived tabs */}
			<div className="flex items-center gap-1 px-4 pb-3">
				<button
					onClick={() => {
						if (showArchived) {
							setShowArchived(false);
							setLoadingThreads(true);
							setThreads([]);
						}
					}}
					className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors ${
						!showArchived
							? "bg-volt text-black"
							: "text-muted-foreground hover:text-foreground"
					}`}
				>
					{t("msgInbox")}
				</button>
				<button
					onClick={() => {
						if (!showArchived) {
							setShowArchived(true);
							setLoadingThreads(true);
							setThreads([]);
						}
					}}
					className={`text-xs font-semibold px-3 py-1 rounded-full transition-colors flex items-center gap-1 ${
						showArchived
							? "bg-amber-500 text-black"
							: "text-muted-foreground hover:text-foreground"
					}`}
				>
					<Archive className="w-3 h-3" />
					{t("msgArchived")}
				</button>
			</div>

			{!pushOptIn.loading &&
				pushOptIn.canPrompt &&
				pushOptIn.dismissed &&
				!showArchived && <PushOptInBanner onEnable={pushOptIn.enable} />}

			{loadingThreads ? (
				<div className="flex items-center justify-center py-20 text-muted-foreground">
					<Loader2 className="w-5 h-5 animate-spin mr-2" />
					{t("loading")}
				</div>
			) : threads.length === 0 ? (
				<div className="text-center py-20 text-muted-foreground px-6">
					{showArchived ? (
						<>
							<Archive className="w-10 h-10 mx-auto mb-3 opacity-40" />
							<p>{t("msgArchivedEmpty")}</p>
						</>
					) : (
						<>
							<MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
							<p>{t("msgNoThreads")}</p>
						</>
					)}
				</div>
			) : (
				<div
					className="flex-1 overflow-y-auto"
					// Close swiped row on scroll
					onScroll={() => {
						if (swipedOpen) setSwipedOpen(null);
					}}
				>
					{threads.map((th) => {
						const mine = th.last_sender_id === me;
						const tx = getRowTranslate(th.thread_id);
						const isLoading = threadActionLoading === th.thread_id;
						const isDragging = liveSwipe?.id === th.thread_id;
						return (
							<div
								key={th.thread_id}
								className="relative overflow-hidden border-b border-border/60"
							>
								{/* Action buttons revealed by swipe */}
								<div
									className="absolute right-0 top-0 h-full flex"
									style={{ width: SNAP_WIDTH }}
									aria-hidden="true"
								>
									<button
										onClick={() => archiveThread(th, !showArchived)}
										disabled={isLoading}
										className="flex-1 flex flex-col items-center justify-center gap-1 bg-amber-500/90 hover:bg-amber-400 text-black text-[10px] font-bold uppercase tracking-wide transition-colors"
									>
										{isLoading ? (
											<Loader2 className="w-5 h-5 animate-spin" />
										) : showArchived ? (
											<ArchiveRestore className="w-5 h-5" />
										) : (
											<Archive className="w-5 h-5" />
										)}
										{showArchived ? t("msgUnarchive") : t("msgArchive")}
									</button>
									<button
										onClick={() => setConfirmDeleteThread(th)}
										disabled={isLoading}
										className="flex-1 flex flex-col items-center justify-center gap-1 bg-blaze/90 hover:bg-blaze text-white text-[10px] font-bold uppercase tracking-wide transition-colors"
									>
										<Trash2 className="w-5 h-5" />
										{t("delete")}
									</button>
								</div>

								{/* Main row (slides left to reveal actions) */}
								<div
									className="relative bg-background"
									style={{
										transform: `translateX(${tx}px)`,
										transition: isDragging ? "none" : "transform 0.2s ease",
										touchAction: "pan-y",
									}}
									onTouchStart={(e) => handleTouchStart(e, th.thread_id)}
									onTouchMove={(e) => handleTouchMove(e, th.thread_id)}
									onTouchEnd={(e) => handleTouchEnd(e, th.thread_id)}
								>
									<button
										onClick={() => {
											if (swipedOpen === th.thread_id) {
												setSwipedOpen(null);
												return;
											}
											openThread(th);
										}}
										className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors group"
									>
										<div
											className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
												th.kind === "feedback"
													? "bg-volt/15 text-volt"
													: th.kind === "support"
														? "bg-amber-500/15 text-amber-300"
														: "bg-secondary text-muted-foreground"
											}`}
										>
											{th.kind === "feedback" ? (
												<Dumbbell className="w-5 h-5" />
											) : th.kind === "support" ? (
												<LifeBuoy className="w-5 h-5" />
											) : (
												<span className="font-heading font-bold">
													{(th.partner_name || "?").charAt(0).toUpperCase()}
												</span>
											)}
										</div>
										<div className="min-w-0 flex-1">
											<div className="flex items-center justify-between gap-2">
												<span className="font-semibold truncate">
													{threadTitle(th)}
												</span>
												<span className="text-xs text-muted-foreground shrink-0">
													{relTime(th.last_at)}
												</span>
											</div>
											<div className="flex items-center justify-between gap-2">
												<span className="text-sm text-muted-foreground truncate">
													{mine ? `${t("msgYou")}: ` : ""}
													{th.last_message}
												</span>
												{th.unread > 0 && (
													<Badge className="bg-volt text-black h-5 min-w-[20px] flex items-center justify-center text-xs p-0 shrink-0">
														{th.unread}
													</Badge>
												)}
											</div>
											{(th.kind === "feedback" || th.kind === "support") &&
												th.partner_name && (
													<span className="text-[11px] text-muted-foreground/70 truncate block">
														{th.partner_name}
														{th.workout_date ? ` · ${th.workout_date}` : ""}
													</span>
												)}
										</div>
										{/* Desktop: quick action buttons (visible on group hover) */}
										<div className="hidden md:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
											<button
												onClick={(e) => {
													e.stopPropagation();
													archiveThread(th, !showArchived);
												}}
												disabled={isLoading}
												title={
													showArchived ? t("msgUnarchive") : t("msgArchive")
												}
												className="p-1.5 rounded-sm text-muted-foreground hover:text-amber-400 hover:bg-amber-400/10 transition-colors"
											>
												{showArchived ? (
													<ArchiveRestore className="w-4 h-4" />
												) : (
													<Archive className="w-4 h-4" />
												)}
											</button>
											<button
												onClick={(e) => {
													e.stopPropagation();
													setConfirmDeleteThread(th);
												}}
												disabled={isLoading}
												title={t("msgDeleteThread")}
												className="p-1.5 rounded-sm text-muted-foreground hover:text-blaze hover:bg-blaze/10 transition-colors"
											>
												<Trash2 className="w-4 h-4" />
											</button>
										</div>
									</button>
								</div>
							</div>
						);
					})}
					{threadsHasMore && (
						<button
							type="button"
							onClick={loadMoreThreads}
							disabled={loadingMoreThreads}
							className="w-full py-3 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50 flex items-center justify-center gap-2"
						>
							{loadingMoreThreads && (
								<Loader2 className="w-4 h-4 animate-spin" />
							)}
							{t("feedbackLoadMore")}
						</button>
					)}
				</div>
			)}
		</div>
	);

	// ---------- THREAD (CHAT) ----------
	const renderThread = () => {
		let lastDay = null;
		const detailResult = active?.detail?.result || null;
		const resultSummary = formatResultSummary(detailResult);
		const feedbackInlineNotes =
			toText(active?.feedback_notes) || toText(detailResult?.notes);
		const contextLabel =
			active?.kind === "support"
				? t("msgSupportRequest")
				: t("msgOriginalFeedback");
		const contextSummary =
			[resultSummary, active?.workout_date].filter(Boolean).join(" · ") ||
			feedbackInlineNotes ||
			active?.exercise_name ||
			"";
		return (
			<div className="flex flex-col h-full">
				{/* Header */}
				<div className="flex items-center gap-3 px-3 py-3 border-b border-border bg-card/60 backdrop-blur sticky top-0 z-10">
					<button
						onClick={() => {
							setActive(null);
							setMessages([]);
							loadThreads();
						}}
						className="p-1.5 -ml-1 text-muted-foreground hover:text-foreground"
						aria-label="Back"
					>
						<ArrowLeft className="w-5 h-5" />
					</button>
					<div
						className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
							active.kind === "feedback"
								? "bg-volt/15 text-volt"
								: active.kind === "support"
									? "bg-amber-500/15 text-amber-300"
									: "bg-secondary text-muted-foreground"
						}`}
					>
						{active.kind === "feedback" ? (
							<Dumbbell className="w-4 h-4" />
						) : active.kind === "support" ? (
							<LifeBuoy className="w-4 h-4" />
						) : (
							<span className="font-heading font-bold text-sm">
								{(active.partner_name || "?").charAt(0).toUpperCase()}
							</span>
						)}
					</div>
					<div className="min-w-0">
						<p className="font-semibold truncate leading-tight">
							{active.kind === "feedback"
								? active.exercise_name || t("feedback")
								: active.kind === "support"
									? active.exercise_name || t("msgSupportRequest")
									: active.partner_name || "—"}
						</p>
						<p className="text-xs text-muted-foreground truncate">
							{active.kind === "feedback"
								? active.partner_name || ""
								: active.kind === "support"
									? active.partner_name || t("msgSupportRequest")
									: t("msgDirect")}
						</p>
					</div>
				</div>

				{/* Exercise context card (feedback / support threads) — collapsible */}
				{(active.kind === "feedback" || active.kind === "support") && (
					<div className="mx-3 mt-2 shrink-0 rounded-sm border border-border bg-secondary/40">
						{/* Compact bar: always visible, tap to expand */}
						<button
							type="button"
							onClick={() => setContextOpen((v) => !v)}
							aria-expanded={contextOpen}
							className="w-full flex items-center gap-2 px-3 py-2 text-left"
						>
							<Dumbbell className="w-3.5 h-3.5 shrink-0 text-volt" />
							<span className="hidden shrink-0 text-[10px] uppercase tracking-widest text-volt sm:inline">
								{contextLabel}
							</span>
							<span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
								{contextSummary}
							</span>
							{active.rpe != null && (
								<Badge
									variant="outline"
									className={`shrink-0 text-xs ${
										active.rpe >= 9
											? "border-blaze/60 text-blaze"
											: "border-border text-muted-foreground"
									}`}
								>
									RPE {active.rpe}
									{active.rpe >= 9 && (
										<AlertTriangle className="w-3 h-3 ml-1" />
									)}
								</Badge>
							)}
							<ChevronDown
								className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${
									contextOpen ? "rotate-180" : ""
								}`}
							/>
						</button>

						{contextOpen && (
							<div className="max-h-[40vh] overflow-y-auto border-t border-border/60">
								<div className="p-3 pt-2">
									<div className="flex items-center gap-2 flex-wrap text-sm">
										<span className="font-medium">
											{active.exercise_name || "—"}
										</span>
										{active.workout_date && (
											<span className="text-xs text-muted-foreground">
												{active.workout_date}
											</span>
										)}
									</div>
									{active.feedback_notes && (
										<p className="mt-1.5 text-sm text-white/90 whitespace-pre-wrap">
											"{active.feedback_notes}"
										</p>
									)}
									{resultSummary && (
										<div className="mt-2 text-xs text-volt">
											<span className="text-muted-foreground">
												{t("resultLabel")}:{" "}
											</span>
											{resultSummary}
										</div>
									)}
									{feedbackInlineNotes && (
										<div className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">
											<span className="text-muted-foreground">
												{t("notes")}:{" "}
											</span>
											{feedbackInlineNotes}
										</div>
									)}

									<div className="mt-2 flex items-center gap-3 flex-wrap">
										{active.detail &&
											(active.detail.prescription || active.detail.result) && (
												<button
													onClick={() => setDetailOpen((v) => !v)}
													className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
												>
													<ChevronDown
														className={`w-4 h-4 transition-transform ${detailOpen ? "rotate-180" : ""}`}
													/>
													{t("feedbackDetail")}
												</button>
											)}
										{user?.role === "athlete" ? (
											<button
												onClick={() => {
													navigate("/workouts", {
														state: {
															diaryFocus: {
																date: active.workout_date || null,
																program_id: active.program_id || null,
																day_id: active.day_id || null,
																exercise_id: active.exercise_id || null,
																exercise_name: active.exercise_name || null,
															},
														},
													});
												}}
												className="inline-flex items-center gap-1 text-xs text-volt hover:underline"
											>
												<ExternalLink className="w-3.5 h-3.5" />
												{t("msgOpenInDiary")}
											</button>
										) : (
											<>
												{active.program_id &&
													(active.program_preview === "expired" ||
													active.program_preview === "missing" ? (
														// Old message: the day's week is no longer delivered, and
														// the preview would land on another workout.
														<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
															<EyeOff className="w-3.5 h-3.5" />
															{active.program_preview === "expired"
																? t("msgProgramNoLongerActive")
																: t("msgProgramNoLongerAvailable")}
														</span>
													) : (
														<button
															onClick={openCoachProgramPreview}
															className="inline-flex items-center gap-1 text-xs text-volt hover:underline"
														>
															<Eye className="w-3.5 h-3.5" />
															{t("feedbackOpenProgram")}
														</button>
													))}
												<button
													onClick={() =>
														navigate(`/athletes/${active.partner_id}`)
													}
													className="inline-flex items-center gap-1 text-xs text-volt hover:underline"
												>
													<ExternalLink className="w-3.5 h-3.5" />
													{t("feedbackOpenAthlete")}
												</button>
											</>
										)}
									</div>
								</div>

								{detailOpen &&
									active.detail &&
									(active.detail.prescription || active.detail.result) && (
										<div className="border-t border-border/60">
											<DetailBoundary>
												<ExerciseDetailAccordion
													exercise={
														active.detail.prescription
															? {
																	...active.detail.prescription,
																	result: active.detail.result,
																}
															: {
																	...(active.detail.result || {}),
																	exercise_name: active.exercise_name,
																	result: active.detail.result,
																}
													}
													language={language}
												/>
											</DetailBoundary>
										</div>
									)}
							</div>
						)}
					</div>
				)}

				{/* Messages */}
				<div
					ref={scrollRef}
					className="flex-1 overflow-y-auto px-3 py-4 space-y-1"
				>
					{loadingThread ? (
						<div className="flex items-center justify-center py-10 text-muted-foreground">
							<Loader2 className="w-5 h-5 animate-spin" />
						</div>
					) : messages.length === 0 ? (
						<div className="text-center py-10 text-sm text-muted-foreground">
							{t("msgEmptyThread")}
						</div>
					) : (
						messages.map((m) => {
							const mine = m.sender_id === me;
							const day = dayLabel(m.created_at);
							const showDay = day !== lastDay;
							lastDay = day;
							return (
								<div key={m.message_id}>
									{showDay && (
										<div className="flex justify-center my-3">
											<span className="text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary/60 rounded-full px-3 py-1">
												{day}
											</span>
										</div>
									)}
									<div
										className={`flex ${mine ? "justify-end" : "justify-start"}`}
									>
										<div
											className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
												mine
													? "bg-volt/20 border border-volt/30 rounded-br-sm"
													: "bg-secondary border border-border rounded-bl-sm"
											}`}
										>
											<p className="whitespace-pre-wrap break-words">
												{m.content}
											</p>
											<div className="text-[10px] text-muted-foreground text-right mt-0.5 inline-flex items-center gap-1 w-full justify-end">
												<span>{fmtTime(m.created_at)}</span>
												{mine &&
													(() => {
														const state = getDeliveryState(m);
														if (state === "read") {
															return (
																<CheckCheck
																	className="w-3.5 h-3.5 text-volt"
																	title={t("msgStatusRead")}
																	aria-label={t("msgStatusRead")}
																/>
															);
														}
														if (state === "delivered") {
															return (
																<CheckCheck
																	className="w-3.5 h-3.5 text-muted-foreground"
																	title={t("msgStatusDelivered")}
																	aria-label={t("msgStatusDelivered")}
																/>
															);
														}
														return (
															<Check
																className="w-3.5 h-3.5 text-muted-foreground"
																title={t("msgStatusSent")}
																aria-label={t("msgStatusSent")}
															/>
														);
													})()}
											</div>
										</div>
									</div>
								</div>
							);
						})
					)}
				</div>

				{/* Composer */}
				<div className="border-t border-border p-2.5 flex items-end gap-2 bg-card/60">
					<textarea
						value={text}
						onChange={(e) => setText(e.target.value)}
						onKeyDown={(e) => {
							if (e.key !== "Enter" || e.shiftKey) return;
							// Nei feedback e nei supporti Invio va a capo: si invia con Cmd/Ctrl+Invio o col bottone.
							if (isFeedbackThread && !(e.metaKey || e.ctrlKey)) return;
							e.preventDefault();
							send();
						}}
						rows={isFeedbackThread ? 3 : 1}
						// Campo da chat, non da modulo: niente AutoFill di contatti/indirizzi, correzione e maiuscole attive.
						name="message-body"
						autoComplete="off"
						autoCorrect="on"
						autoCapitalize="sentences"
						spellCheck
						inputMode="text"
						enterKeyHint={isFeedbackThread ? "enter" : "send"}
						placeholder={t("msgWritePlaceholder")}
						className="flex-1 resize-none bg-secondary border border-border rounded-2xl px-4 py-2.5 text-sm max-h-32 focus:outline-none focus:border-volt/50"
					/>
					<Button
						onClick={send}
						disabled={sending || !text.trim()}
						className="bg-volt text-black rounded-full h-10 w-10 p-0 shrink-0 hover:bg-volt/90"
						aria-label={t("msgSend")}
					>
						{sending ? (
							<Loader2 className="w-4 h-4 animate-spin" />
						) : (
							<Send className="w-4 h-4" />
						)}
					</Button>
				</div>
			</div>
		);
	};

	return (
		<Layout>
			<div className="max-w-2xl mx-auto h-full min-h-[70vh] flex flex-col md:border-x border-border bg-background">
				{active ? renderThread() : renderList()}
			</div>

			<Dialog
				open={programPreviewOpen}
				onOpenChange={(open) => {
					setProgramPreviewOpen(open);
					if (!open) {
						setProgramPreviewState(null);
					}
				}}
			>
				<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-5xl max-h-[90vh] overflow-hidden">
					<DialogHeader className="pt-3 md:pt-0">
						<DialogTitle className="font-heading">
							{t("athleteProgramsPreviewTitle")} · {active?.partner_name || "-"}
						</DialogTitle>
					</DialogHeader>

					<div className="mx-auto w-[390px] max-w-full rounded-[28px] border border-border bg-black p-2 shadow-2xl shadow-black/60">
						<div className="rounded-[22px] border border-border bg-background h-[72vh] overflow-y-auto">
							{programPreviewLoading ? (
								<div className="h-full flex items-center justify-center text-sm text-muted-foreground">
									<Loader2 className="w-5 h-5 animate-spin mr-2" />
									{t("loading")}
								</div>
							) : programPreviewState?.program ? (
								<AthleteDashboard
									previewState={programPreviewState}
									containerClassName="max-w-none mx-0 px-3 pb-6"
								/>
							) : (
								<div className="h-full flex items-center justify-center text-sm text-muted-foreground">
									{t("noAssignedProgramsPreview")}
								</div>
							)}
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* New conversation: contact picker */}
			<Dialog open={newOpen} onOpenChange={setNewOpen}>
				<DialogContent className="bg-card border-border">
					<DialogHeader>
						<DialogTitle>{t("msgSelectContact")}</DialogTitle>
					</DialogHeader>
					<div className="relative">
						<Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
						<Input
							value={contactSearchQuery}
							onChange={(e) => setContactSearchQuery(e.target.value)}
							placeholder={t("searchAthletes")}
							className="pl-9 bg-secondary border-transparent rounded-sm"
						/>
					</div>
					{contacts.length === 0 ? (
						<p className="text-sm text-muted-foreground py-4">
							{t("msgNoContacts")}
						</p>
					) : filteredContacts.length === 0 ? (
						<p className="text-sm text-muted-foreground py-4">
							{t("tryDifferentSearch")}
						</p>
					) : (
						<div className="space-y-1 max-h-80 overflow-y-auto">
							{filteredContacts.map((c) => (
								<button
									key={c.user_id}
									onClick={() => startDirect(c)}
									className="w-full text-left px-3 py-2.5 rounded-sm flex items-center gap-3 hover:bg-white/5"
								>
									<div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-muted-foreground">
										<User className="w-4 h-4" />
									</div>
									<div className="min-w-0">
										<p className="font-medium truncate">{c.name}</p>
										{c.email && (
											<p className="text-xs text-muted-foreground truncate">
												{c.email}
											</p>
										)}
									</div>
								</button>
							))}
						</div>
					)}
				</DialogContent>
			</Dialog>

			{/* Delete conversation confirmation */}
			<AlertDialog
				open={!!confirmDeleteThread}
				onOpenChange={(open) => {
					if (!open) setConfirmDeleteThread(null);
				}}
			>
				<AlertDialogContent className="bg-card border-border">
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("msgDeleteThreadConfirmTitle")}
						</AlertDialogTitle>
						<AlertDialogDescription className="text-muted-foreground">
							{t("msgDeleteThreadConfirmBody")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel className="rounded-sm">
							{t("cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								confirmDeleteThread && deleteThreadForMe(confirmDeleteThread)
							}
							className="bg-blaze text-white hover:bg-blaze/90 rounded-sm"
						>
							<Trash2 className="w-4 h-4 mr-1.5" />
							{t("delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Layout>
	);
}
